import React, { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import EmptyResultSetMessage from 'components/@common/EmptyResultSetMessage';
import { ModalContainer, ModalFooter, ModalHeader } from 'components/@common/Modal';
import ProductLicenseCard from 'components/@common/ProductLicenses/ProductLicenseCard';
import UnsavedChangeIcon from 'components/@common/ProductLicenses/UnsavedChangeIcon';
import { uniq } from 'lodash/fp';
import { Box, Container } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import GET_ADMIN_USER_MANAGEMENT_USERS from 'graph/user/get-admin-user-management-users';
import GET_ME from 'graph/user/get-me-query';
import UPDATE_USER_LICENSES from 'graph/user/update-user-licenses-mutation';
import SEND_LICENSE_ASSIGNMENT_EMAIL from 'graph/email/send-license-assignment-mutation';
import useParseActionRouteParams from 'hooks/route-action/use-parse-action-route-params';
import useProductCache from 'store/cache/product-cache';
import useUiStore from 'store/ui';
import useUserStore from 'store/user';
import buildEmailUsers from 'utilities/build-email-users';
import { OrganizationLicenseUsage } from 'types/schema-generated-types';
import { gql } from 'types/__generated__';
import { LicenseAction, ProductType, BulkLicenseActionInput } from 'types/__generated__/graphql';
import PageContainer from 'components/@layout/PageContainer';
import Alert from '@material-ui/lab/Alert';
import licenseUpdateReducer, { ActionType as UpdateLicenseActionType } from './reducer';
import { AlertVariant } from 'types/enums';

const ManageUserProductsModalQuery = gql(/* GraphQL */ `
  query getUserProducts($where: UserWhereInput!) {
    user(where: $where) {
      id
      username
      firstName
      lastName
      licenseUsage {
        assignedCount
        manualAssignment
        trial
        product {
          id
          code
          family {
            id
            code
          }
        }
        productCurriculum {
          id
          code
        }
      }
      organizationAssociations {
        organization {
          id
          name
          licenseUsage {
            product {
              id
              code
              type
              family {
                id
                code
              }
            }
            availableCount
            siteLicensed
          }
        }
      }
    }
  }
`);

const ManageUserProductsModal = () => {
  const classes = useStyles();
  const { t } = useTranslation('EditUserModal');
  const { userId } = useParseActionRouteParams();
  const [, { closeModal, openAlert }] = useUiStore();
  const [{ me }] = useUserStore();
  const [{ findProductsByProductFamilyId }] = useProductCache();

  const [licenseUpdates, dispatch] = useReducer(licenseUpdateReducer, []);

  const { data, loading, error } = useQuery(ManageUserProductsModalQuery, {
    fetchPolicy: 'no-cache',
    variables: { where: { id: +userId } }
  });

  const [updateLicenses, { data: updateResponse, loading: updateLoading }] = useMutation(
    UPDATE_USER_LICENSES,
    {
      refetchQueries: [
        { query: GET_ADMIN_USER_MANAGEMENT_USERS },
        // GET_ME uses a specialized license usage for App cards, refetch if current user is the one being updated
        ...(me?.id === +userId ? [{ query: GET_ME }] : [])
      ]
    }
  );

  const [sendLicenseAssignmentEmail] = useMutation(SEND_LICENSE_ASSIGNMENT_EMAIL);

  const assignedProductFamilyCurriculumMap = useMemo(() => {
    const userLicenseUsage = data?.user?.licenseUsage ?? [];

    const result = new Map<number, { id: number; manualAssignment: boolean }[]>();
    userLicenseUsage
      .filter(lu => lu.assignedCount > 0 && !!lu.productCurriculum && !lu.trial)
      .forEach(x => {
        if (!x.productCurriculum) return;
        const existing = result.get(x.product.family.id) ?? [];
        result.set(x.product.family.id, [
          ...existing,
          { id: x.productCurriculum.id, manualAssignment: x.manualAssignment }
        ]);
      });

    return result;
  }, [data]);

  const handleLicenseAssign = (productFamilyId: number, productCurriculumId: number | number[]) =>
    dispatch({
      type: UpdateLicenseActionType.ADD_UPDATES,
      payload: {
        action: LicenseAction.ASSIGN,
        productFamilyId,
        productCurriculumIds: ([] as number[]).concat(productCurriculumId)
      }
    });

  const handleLicenseUnassign = (
    productFamilyId: number,
    productCurriculumId: number | number[]
  ) => {
    dispatch({
      type: UpdateLicenseActionType.ADD_UPDATES,
      payload: {
        action: LicenseAction.UNASSIGN,
        productFamilyId,
        productCurriculumIds: ([] as number[]).concat(productCurriculumId)
      }
    });
  };

  const organizationLicenseUsage =
    data?.user?.organizationAssociations?.[0]?.organization.licenseUsage ?? [];

  const productFamilyIds = useMemo(() => {
    // Don't think it should matter which organization we use here, since child organizations
    // inherit availabie licenses from their parent.
    const organizationLicenseUsage =
      data?.user?.organizationAssociations?.[0]?.organization.licenseUsage ?? [];

    return uniq(
      organizationLicenseUsage
        .filter(lu => lu.product.type === ProductType.USER)
        .map(lu => lu.product.family.id)
    );
  }, [data]);

  const handleSubmit = useCallback(async () => {
    const licenseAssignment: BulkLicenseActionInput[] = licenseUpdates.map(u => ({
      userId: +userId,
      action: u.action,
      productCurriculum: { id: u.productCurriculumId }
    }));

    await updateLicenses({ variables: { data: licenseAssignment } });

    const productNames = uniq(
      licenseUpdates.reduce((acc, lu) => {
        if (lu.action !== LicenseAction.ASSIGN) return acc;
        const products = findProductsByProductFamilyId(lu.productFamilyId);
        const product = products.find(p =>
          p.curriculums?.find(c => c.id === lu.productCurriculumId)
        );
        return product?.name ? [...acc, product.name] : acc;
      }, [] as string[])
    );

    if (me && data?.user && productNames.length > 0) {
      const { sender, recipient } = buildEmailUsers(me, data.user);
      await sendLicenseAssignmentEmail({
        variables: { data: { sender, recipient, productNames } }
      });
    }

    openAlert({
      variant: AlertVariant.SUCCESS,
      title: t('User Products Updated'),
      text: t(`The user's assigned products have been successfully updated.`)
    });
  }, [
    data,
    findProductsByProductFamilyId,
    licenseUpdates,
    me,
    openAlert,
    sendLicenseAssignmentEmail,
    t,
    updateLicenses,
    userId
  ]);

  useEffect(() => {
    if (updateResponse?.bulkLicenseAction) closeModal();
  }, [updateResponse, closeModal]);

  return (
    <PageContainer loading={loading} error={error}>
      <ModalContainer
        className={classes.body}
        Header={<ModalHeader title={t('Manage User Products')} />}
        Footer={
          <ModalFooter
            onCancel={closeModal}
            onSubmit={productFamilyIds.length > 0 ? handleSubmit : undefined}
            submitDisabled={updateLoading}
          >
            <Box marginLeft={4}>
              {licenseUpdates.length > 0 && (
                <Alert severity="warning" icon={<UnsavedChangeIcon />} variant="outlined">
                  {t('For changes to take effect you must save changes')}
                </Alert>
              )}
            </Box>
          </ModalFooter>
        }
      >
        {productFamilyIds.length === 0 && (
          <EmptyResultSetMessage
            style={{ marginTop: '12px' }}
            message={t('No available licenses', { ns: 'EmptyResultSetMessage' })}
            subtitle={t(`There are no licenses available to assign to this user.`, {
              ns: 'EmptyResultSetMessage'
            })}
          />
        )}
        {productFamilyIds.map(productFamilyId => {
          const assignedProductCurriculums = assignedProductFamilyCurriculumMap.get(
            productFamilyId
          );

          const assignedProductCurriculumIds = assignedProductCurriculums?.map(x => x.id);
          const manualProductCurriculumIds = assignedProductCurriculums
            ?.filter(x => x.manualAssignment)
            .map(x => x.id);

          return (
            <Container key={productFamilyId} maxWidth="lg" className={classes.cardContainer}>
              <ProductLicenseCard
                productType={ProductType.USER}
                productFamilyId={productFamilyId}
                assignedProductCurriculumIds={assignedProductCurriculumIds}
                manuallyAssignedProductCurriculumIds={manualProductCurriculumIds}
                pendingUpdates={licenseUpdates.filter(y => y.productFamilyId === productFamilyId)}
                organizationLicenseUsage={organizationLicenseUsage as OrganizationLicenseUsage[]}
                onLicenseAssign={handleLicenseAssign}
                onLicenseUnassign={handleLicenseUnassign}
              />
            </Container>
          );
        })}
      </ModalContainer>
    </PageContainer>
  );
};

const useStyles = makeStyles(theme => {
  return {
    body: {
      background: '#EEEEEE'
    },
    cardContainer: {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2)
    },
    nextButton: {
      marginRight: theme.spacing(6)
    },
    cancelButton: {
      marginRight: theme.spacing(3)
    },
    buttonLabels: {
      fontSize: '1rem',
      textTransform: 'initial'
    },
    saveChangesMessage: {
      marginLeft: theme.spacing(6)
    }
  };
});

export default ManageUserProductsModal;
